import { z } from "zod";

/**
 * `CLIENT-OFFER-TERMS` (founder D-1102-3, « Both see all now. »; the architect's orders of 1 November 2026): **the terms of the offer the buyer retained, served on the retained offer
 * from the bank's OWN offer record**, so the client sees the same figures here, on `GET /api/v1/me/offers` and on the bank's own read of the same offer.
 *
 * Additive: the existing fields of `retainedOffer` are untouched (a whole-answer contract is for after 8 November). `terms` is composed by `lending` from the stored `lending.offers` columns and passed
 * through by `dossier`; the browser computes nothing (D-1020-7).
 *
 * - `instalmentAmount` is the STORED `instalment_amount`, byte for byte: **insurance INCLUDED** (canon `02-domain-model.md` 2.6.1, « Mensualité … insurance included »), with the stored
 *   `insuranceInstalmentAmount` raw beside it. No new sum.
 * - `amountFinancedAmount` is `borrowedAmountOf` (`@hakili/domain-types`) over the fields the offer already carries: the vehicle part, each fee the bank agreed to carry, a financed warranty.
 * - `nominalRatePct` is the shape `GET /api/v1/me/offers` serves (a number, `10.8`): one shape for the client.
 * - `labels` are the words of the four rows, served (« Montant financé », « Durée », « Taux nominal », « Mensualité »). Bank-internal terms (the grid, the TAEG, the LTVs, the gaps) stay hidden.
 */
export const CLIENT_OFFER_TERMS_LABELS = {
  amountFinanced: "Montant financé",
  duration: "Durée",
  nominalRate: "Taux nominal",
  instalment: "Mensualité",
} as const;

function buildSchemas(object: typeof z.strictObject) {
  const terms = object({
    amountFinancedAmount: z.number().int().min(0),
    durationMonths: z.number().int().min(1),
    /** As `GET /api/v1/me/offers` serves it: a number (`10.8`; the column has one decimal). */
    nominalRatePct: z.number().min(0).max(40),
    /** The stored instalment, insurance INCLUDED. */
    instalmentAmount: z.number().int().min(0),
    /** The insurance part of it, raw. */
    insuranceInstalmentAmount: z.number().int().min(0),
    labels: object({
      amountFinanced: z.literal(CLIENT_OFFER_TERMS_LABELS.amountFinanced),
      duration: z.literal(CLIENT_OFFER_TERMS_LABELS.duration),
      nominalRate: z.literal(CLIENT_OFFER_TERMS_LABELS.nominalRate),
      instalment: z.literal(CLIENT_OFFER_TERMS_LABELS.instalment),
    }),
  });
  return { terms };
}

const strict = buildSchemas(z.strictObject);
const loose = buildSchemas(z.looseObject);

export const clientOfferTermsSchema = strict.terms;
/** The READ twin: what the client parses the live `retainedOffer.terms` with. */
export const clientOfferTermsReadSchema = loose.terms;
export type ClientOfferTerms = z.infer<typeof clientOfferTermsSchema>;
